// Go WASM加载器和桥接API
let initialized = false;

// 用于存储Go WebAssembly实例
let goWasm = null;

// 监控Go程序状态的变量
let monitorInterval = null;
let monitorFailCount = 0;
const MAX_MONITOR_FAILURES = 3;

// 标志位，用于标记Go程序是否已退出
let goHasExited = false;
let pendingReload = false;

// 加载Go WASM模块
export const loadGoModule = async () => {
  // 如果已经在重新加载中，等待
  if (pendingReload) {
    console.log("已有加载任务在进行中，跳过...");
    return;
  }

  // 如果已经初始化且Go程序正常，直接返回
  if (initialized && !goHasExited) {
    return;
  }

  // 标记正在加载
  pendingReload = true;

  try {
    console.log("开始加载WebAssembly模块...");

    // 重置状态
    goHasExited = false;
    initialized = false;

    // 清除任何现有的监控
    if (monitorInterval) {
      clearInterval(monitorInterval);
      monitorInterval = null;
    }

    // 重置失败计数
    monitorFailCount = 0;

    // 确保Go类存在
    if (typeof window.Go === "undefined") {
      console.error("错误: 找不到window.Go类。请确保wasm_exec.js已正确加载");
      throw new Error("Go WebAssembly支持未找到。请确保wasm_exec.js已加载");
    }

    // 创建Go实例
    const go = new window.Go();

    // 监听Go退出事件
    go.exited = false;
    const originalExit = go._inst?.exports?.runtime?.exit;
    if (go._inst && go._inst.exports && go._inst.exports.runtime) {
      go._inst.exports.runtime.exit = (...args) => {
        console.warn("Go程序退出被捕获", args);
        go.exited = true;
        goHasExited = true;
        if (originalExit) {
          originalExit(...args);
        }
        // 10秒后尝试重新加载
        setTimeout(() => reloadGoModule(), 10000);
      };
    }

    console.log("Go实例创建成功");

    // 获取wasm文件
    console.log("正在获取WebAssembly文件...");
    // 使用相对路径，并添加根据当前环境自动选择路径的逻辑
    let wasmURL = "/assets/projects/chatterbox/chatterbox.wasm";

    // 检测是否在生产环境，根据域名调整路径
    if (
      window.location.hostname !== "localhost" &&
      window.location.hostname !== "127.0.0.1"
    ) {
      // 尝试使用相对根路径
      wasmURL = `${window.location.origin}/assets/projects/chatterbox/chatterbox.wasm`;
      console.log("生产环境，使用完整URL路径");
    }

    console.log("WebAssembly URL:", wasmURL);

    const wasmResponse = await fetch(wasmURL);
    console.log(
      "WebAssembly文件获取响应状态:",
      wasmResponse.status,
      wasmResponse.statusText
    );

    if (!wasmResponse.ok) {
      throw new Error(
        `获取WebAssembly文件失败: ${wasmResponse.status} ${wasmResponse.statusText}`
      );
    }

    const wasmBytes = await wasmResponse.arrayBuffer();
    console.log(
      "WebAssembly文件数据获取成功，大小:",
      wasmBytes.byteLength,
      "字节"
    );

    // 初始化wasm模块
    console.log("正在实例化WebAssembly模块...");
    const result = await WebAssembly.instantiate(wasmBytes, go.importObject);
    console.log("WebAssembly模块实例化成功");

    // 运行go程序
    console.log("正在运行Go WebAssembly程序...");

    // 捕获可能的错误
    try {
      go.run(result.instance);
      console.log("Go WebAssembly程序已启动");
    } catch (runError) {
      console.error("运行Go WebAssembly程序时出错:", runError);
      goHasExited = true;
      throw runError;
    }

    // 检查初始化是否成功
    console.log("正在检查Go命名空间:", window.go);
    console.log("正在检查chatterbox API:", window.go?.chatterbox);

    if (!window.go || !window.go.chatterbox) {
      throw new Error("WebAssembly模块已加载但chatterbox API不可用");
    }

    // 创建桥接API
    goWasm = window.go.chatterbox;
    console.log("获取到Go导出的模块:", Object.keys(goWasm));

    // 添加帮助方法
    console.log("正在设置桥接API...");
    setupBridgeAPI();

    // 设置监控Go程序的定时器
    monitorInterval = setInterval(() => {
      try {
        // 检查Go是否已退出
        if (goHasExited) {
          console.warn("Go程序已退出，正在准备重新加载...");
          reloadGoModule();
          return;
        }

        // 简单检查window.go是否存在，而不是尝试调用方法
        if (!window.go || !window.go.chatterbox) {
          monitorFailCount++;
          console.warn(
            `Go命名空间丢失 (${monitorFailCount}/${MAX_MONITOR_FAILURES})`
          );

          if (monitorFailCount >= MAX_MONITOR_FAILURES) {
            console.warn("达到最大失败次数，尝试重新加载...");
            reloadGoModule();
          }
          return;
        }

        // 重置失败计数
        monitorFailCount = 0;
      } catch (error) {
        monitorFailCount++;
        console.error(
          `监控检查失败 (${monitorFailCount}/${MAX_MONITOR_FAILURES}):`,
          error
        );

        if (monitorFailCount >= MAX_MONITOR_FAILURES) {
          console.warn("达到最大失败次数，尝试重新加载...");
          reloadGoModule();
        }
      }
    }, 30000); // 增加到每30秒检查一次，减少调用频率

    initialized = true;
    console.log("WebAssembly模块加载完成");
  } catch (error) {
    console.error("加载Go WebAssembly模块失败:", error);
    throw error;
  } finally {
    // 清除加载标记
    pendingReload = false;
  }
};

// 尝试重新加载Go模块
const reloadGoModule = async () => {
  // 如果已经在重新加载中，直接返回
  if (pendingReload) {
    console.log("已有重新加载任务在进行中，跳过...");
    return;
  }

  try {
    console.log("正在尝试重新加载Go WebAssembly模块...");

    // 标记需要重新加载
    pendingReload = true;

    // 重置状态
    initialized = false;
    goHasExited = false;

    // 清除监控
    if (monitorInterval) {
      clearInterval(monitorInterval);
      monitorInterval = null;
    }

    // 重置失败计数
    monitorFailCount = 0;

    // 重新加载模块
    await loadGoModule();
  } catch (error) {
    console.error("重新加载Go WebAssembly模块失败:", error);
  } finally {
    // 清除加载标记
    pendingReload = false;
  }
};

// 设置桥接API，确保Go函数可以在JavaScript中使用
function setupBridgeAPI() {
  if (!window.go) {
    window.go = {};
  }

  if (!window.go.chatterbox) {
    window.go.chatterbox = {};
  }

  // 确保Go公开的方法都有适当的JavaScript包装
  const bridge = window.go.chatterbox;
  console.log("正在注册以下Go函数:", Object.keys(goWasm));

  // 为所有函数添加安全包装器
  function safeWrap(fn, fnName) {
    return async (...args) => {
      // 检查是否需要重新加载
      if (goHasExited || !window.go || !window.go.chatterbox) {
        console.warn(`Go程序已退出，正在自动重新加载... (调用: ${fnName})`);
        await reloadGoModule();

        // 如果仍然无法使用，抛出错误
        if (goHasExited || !window.go || !window.go.chatterbox) {
          throw new Error("Go模块未能正确加载，请刷新页面重试");
        }
      }

      try {
        // 尝试调用Go函数
        return await fn(...args);
      } catch (error) {
        // 特殊处理"Go program has exited"错误
        if (
          error.toString().includes("Go program has exited") ||
          error.toString().includes("invalid value") ||
          error.toString().includes("panic:")
        ) {
          console.error(`Go程序在调用${fnName}时出错:`, error);
          goHasExited = true;

          // 重新加载并重试一次
          await reloadGoModule();

          // 如果重新加载成功，再次尝试调用
          if (!goHasExited && window.go && window.go.chatterbox) {
            console.log(`尝试重新调用 ${fnName} 函数...`);
            try {
              return await fn(...args);
            } catch (retryError) {
              console.error(`重试调用${fnName}失败:`, retryError);
              throw new Error(`Go函数${fnName}执行失败: ${retryError.message}`);
            }
          } else {
            throw new Error(`Go程序已退出，无法执行${fnName}，请刷新页面`);
          }
        }

        // 其他错误正常抛出
        throw error;
      }
    };
  }

  // 生成密钥对
  bridge.generateKeyPair = safeWrap(async () => {
    console.log("调用Go函数: GenerateKeyPair");
    const result = await goWasm.GenerateKeyPair();
    console.log("GenerateKeyPair结果:", result);
    return {
      publicKey: result.PublicKey,
      privateKey: result.PrivateKey.Key,
    };
  }, "GenerateKeyPair");

  // DH密钥交换
  bridge.dhCombine = safeWrap(async (publicKey, privateKey) => {
    console.log("调用Go函数: DHCombine", { publicKey, privateKey });
    return await goWasm.DHCombine(publicKey, privateKey);
  }, "DHCombine");

  // 派生密钥
  bridge.deriveKey = safeWrap(async (baseKey, label) => {
    console.log("调用Go函数: DeriveKey", { baseKey, label });
    return await goWasm.DeriveKey(baseKey, label);
  }, "DeriveKey");

  // 合并密钥
  bridge.combineKeys = safeWrap(async (keys) => {
    console.log("调用Go函数: CombineKeys", { keys });
    return await goWasm.CombineKeys(keys);
  }, "CombineKeys");

  // 生成新的IV - 使用JavaScript原生实现
  bridge.newIV = async () => {
    console.log("使用内置方法生成IV...");
    // 使用Web Crypto API生成随机IV
    const iv = new Uint8Array(12); // GCM模式使用12字节IV
    window.crypto.getRandomValues(iv);
    console.log("IV生成成功:", iv.length + " bytes");
    return iv;
  };

  // 加密
  bridge.authenticatedEncrypt = safeWrap(
    async (plaintext, key, additionalData, iv) => {
      try {
        // 确保所有输入都是Uint8Array类型
        if (!(plaintext instanceof Uint8Array)) {
          throw new Error("明文必须是Uint8Array类型");
        }

        if (!(key instanceof Uint8Array)) {
          throw new Error("密钥必须是Uint8Array类型");
        }

        if (!(additionalData instanceof Uint8Array)) {
          throw new Error("附加数据必须是Uint8Array类型");
        }

        if (!(iv instanceof Uint8Array)) {
          throw new Error("IV必须是Uint8Array类型");
        }

        console.log("调用Go函数: AuthenticatedEncrypt", {
          plaintextLength: plaintext.length + " bytes",
          keyLength: key.length + " bytes",
          additionalDataLength: additionalData.length + " bytes",
          ivLength: iv.length + " bytes",
        });

        const result = await goWasm.AuthenticatedEncrypt(
          plaintext,
          key,
          additionalData,
          iv
        );

        return result;
      } catch (error) {
        console.error("AuthenticatedEncrypt调用失败:", error);
        throw error;
      }
    },
    "AuthenticatedEncrypt"
  );

  // 解密
  bridge.authenticatedDecrypt = safeWrap(
    async (ciphertext, key, additionalData, iv) => {
      try {
        // 验证并转换参数类型
        // 确保密文是Uint8Array
        let safeCiphertext;
        if (!(ciphertext instanceof Uint8Array)) {
          console.warn(
            "authenticatedDecrypt: 密文不是Uint8Array类型，尝试转换"
          );
          if (ciphertext instanceof ArrayBuffer) {
            safeCiphertext = new Uint8Array(ciphertext);
          } else if (typeof ciphertext === "object" && ciphertext !== null) {
            if (ArrayBuffer.isView(ciphertext)) {
              safeCiphertext = new Uint8Array(ciphertext.buffer);
            } else {
              console.error("无法将密文转换为Uint8Array:", typeof ciphertext);
              throw new Error("密文必须是Uint8Array类型");
            }
          } else {
            console.error("无法处理的密文类型:", typeof ciphertext);
            throw new Error("密文必须是Uint8Array类型");
          }
        } else {
          safeCiphertext = ciphertext;
        }

        // 确保密钥是Uint8Array
        let safeKey;
        if (!(key instanceof Uint8Array)) {
          console.warn(
            "authenticatedDecrypt: 密钥不是Uint8Array类型，尝试转换"
          );
          if (key instanceof ArrayBuffer) {
            safeKey = new Uint8Array(key);
          } else if (typeof key === "object" && key !== null) {
            if (ArrayBuffer.isView(key)) {
              safeKey = new Uint8Array(key.buffer);
            } else {
              console.error("无法将密钥转换为Uint8Array:", typeof key);
              throw new Error("密钥必须是Uint8Array类型");
            }
          } else {
            console.error("无法处理的密钥类型:", typeof key);
            throw new Error("密钥必须是Uint8Array类型");
          }
        } else {
          safeKey = key;
        }

        // 确保附加数据是Uint8Array
        let safeAdditionalData;
        if (!(additionalData instanceof Uint8Array)) {
          console.warn(
            "authenticatedDecrypt: 附加数据不是Uint8Array类型，尝试转换"
          );
          if (additionalData instanceof ArrayBuffer) {
            safeAdditionalData = new Uint8Array(additionalData);
          } else if (
            typeof additionalData === "object" &&
            additionalData !== null
          ) {
            if (ArrayBuffer.isView(additionalData)) {
              safeAdditionalData = new Uint8Array(additionalData.buffer);
            } else {
              console.warn("使用空的附加数据代替");
              safeAdditionalData = new Uint8Array(0);
            }
          } else {
            console.warn("使用空的附加数据代替");
            safeAdditionalData = new Uint8Array(0);
          }
        } else {
          safeAdditionalData = additionalData;
        }

        // 确保IV是Uint8Array
        let safeIV;
        if (!(iv instanceof Uint8Array)) {
          console.warn("authenticatedDecrypt: IV不是Uint8Array类型，尝试转换");
          if (iv instanceof ArrayBuffer) {
            safeIV = new Uint8Array(iv);
          } else if (typeof iv === "object" && iv !== null) {
            if (ArrayBuffer.isView(iv)) {
              safeIV = new Uint8Array(iv.buffer);
            } else {
              console.error("无法将IV转换为Uint8Array:", typeof iv);
              throw new Error("IV必须是Uint8Array类型");
            }
          } else {
            console.error("无法处理的IV类型:", typeof iv);
            throw new Error("IV必须是Uint8Array类型");
          }
        } else {
          safeIV = iv;
        }

        // 参数验证
        if (!safeCiphertext || safeCiphertext.length === 0) {
          throw new Error("密文不能为空");
        }

        if (!safeKey || safeKey.length === 0) {
          throw new Error("解密密钥无效");
        }

        if (!safeIV || safeIV.length === 0) {
          throw new Error("IV无效");
        }

        // 检查Go是否可用
        if (!window.go || !window.go.chatterbox || !goWasm) {
          throw new Error("Go WASM环境不可用");
        }

        console.log("authenticatedDecrypt调用Go函数:", {
          ciphertextLength: safeCiphertext.length + " bytes",
          keyLength: safeKey.length + " bytes",
          additionalDataLength: safeAdditionalData.length + " bytes",
          ivLength: safeIV.length + " bytes",
        });

        // 调用Go函数解密
        const decryptedBytes = await goWasm.AuthenticatedDecrypt(
          safeCiphertext,
          safeKey,
          safeAdditionalData,
          safeIV
        );

        // 确保结果是Uint8Array
        if (!(decryptedBytes instanceof Uint8Array)) {
          console.warn("解密结果不是Uint8Array类型，尝试转换");
          if (decryptedBytes instanceof ArrayBuffer) {
            return new Uint8Array(decryptedBytes);
          } else {
            throw new Error("解密结果不是预期的类型");
          }
        }

        return decryptedBytes;
      } catch (error) {
        console.error("AuthenticatedDecrypt调用失败:", error);
        throw error;
      }
    },
    "AuthenticatedDecrypt"
  );

  // 保持活跃 - 简化实现，避免多层调用
  bridge.KeepAlive = () => {
    // 不尝试调用Go，直接返回
    return !goHasExited;
  };

  console.log("桥接API设置完成");
}

// 检查WASM是否已加载
export const isWasmLoaded = () => initialized && !goHasExited;

// 重置WASM状态
export const resetWasmState = async () => {
  console.log("正在重置WASM状态...");
  goHasExited = true;
  await reloadGoModule();
  return isWasmLoaded();
};
